import { useState, useMemo, useEffect, useCallback } from 'react';
import { Button, Box, Typography, Menu, Autocomplete, TextField } from '@mui/material';
import { Plus } from 'lucide-react';
import DataTable from '../common/DataTable/index';
import Search from '../common/Search';
import { fetchAreas } from '../../../apis/Masters/Area';
import ExportExcel from '../common/ExportExcel';
import { apiFetch } from '../../../services/api';
import { useResponsive, userScreenAccess } from '../../../utils/MasterUtils';
import { fetchUserRightsMenu, saveUserRights } from '../../../apis/UserRights';
import { fetchGlobalTypesCombo } from '../../../apis/Masters/GlobalType';
import { MastersId } from '../../../constants/constant';
import { FilterFieldSx } from '../../../constants/style';
import { fetchRights } from '../../../utils';

let _editRowHandler = null;

const permissionColumns = [
  { label: 'List Access', key: 'hasListPermission' },
  { label: 'Add Access', key: 'hasAddPermission' },
  { label: 'Edit Access', key: 'hasEditPermission' },
  { label: 'Delete Access', key: 'hasDeletePermission' }
];

const RoleList = () => {
  const [screens, setScreens] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuItems, setMenuItems] = useState([]);
  const [roleId, setRoleId] = useState(0);
  const [roles, setRoles] = useState([]);

  const { isMobile } = useResponsive();
  const userAccess = userScreenAccess();
  const canEdit = Boolean(userAccess?.hasEditPermission);
  const userRole = (() => {
    try {
      const raw = localStorage.getItem('userDetails');
      return raw ? JSON.parse(raw)?.role : null;
    } catch {
      return null;
    }
  })();

  const getRowKey = (row) => `${row.roleGUID}_${row.menuName}_${row.menuItemName}`;

  const handlePermissionChange = useCallback(
    (row, permissionKey) => {
      if (!canEdit) return;
      const rowKey = getRowKey(row);
      const newValue = !row[permissionKey];

      setScreens((prevScreens) => {
        const updatedScreens = prevScreens.map((item) =>
          getRowKey(item) === rowKey ? { ...item, [permissionKey]: newValue } : item
        );

        const permissions = updatedScreens.filter(
          (item) =>
            item.hasListPermission ||
            item.hasAddPermission ||
            item.hasEditPermission ||
            item.hasDeletePermission
        );
        setMenuItems(permissions);

        return updatedScreens;
      });
    },
    [canEdit]
  );

  const fetchRightsMenu = async (roleId) => {
    if (roleId <= 0) {
      window.showSnackbar?.({
        message: 'Please select a role to view permissions.',
        variant: 'warning'
      });
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetchUserRightsMenu(roleId);
      setScreens(response || []);
    } catch (err) {
      console.log('Error fetching user rights:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const headers = useMemo(
    () => [
      {
        label: 'Menu Name',
        key: 'menuName',
        minWidth: 150,
        sortable: true,
        align: 'left',
        resizable: true
      },
      {
        label: 'Menu Item Name',
        key: 'menuItemName',
        minWidth: 150,
        sortable: true,
        align: 'left',
        resizable: true
      },
      ...permissionColumns.map((column) => ({
        ...column,
        minWidth: 100,
        sortable: false,
        align: 'center',
        render: (_val, row) => (
          <input
            type="checkbox"
            className="form-check-input"
            checked={Boolean(row[column.key])}
            disabled={!canEdit}
            onClick={(e) => e.stopPropagation()}
            onChange={() => handlePermissionChange(row, column.key)}
            style={{ cursor: canEdit ? 'pointer' : 'not-allowed' }}
          />
        )
      }))
    ],
    [canEdit, handlePermissionChange]
  );

  const saveChanges = async () => {
    if (menuItems.length === 0) {
      window.showSnackbar?.({ message: 'No permission changes to save.', variant: 'info' });
      return;
    }
    const payload = {
      roleGUID: roleId,
      menuItems
    };
    setIsSaving(true);
    try {
      const response = await saveUserRights(payload, 'Permissions updated successfully.', '');
      if (response && response.success) {
        setMenuItems([]);
        fetchRightsMenu(roleId);
      }
    } catch (err) {
      window.showSnackbar?.({
        message: err?.message || 'Failed to save permission changes.',
        variant: 'error'
      });
      console.log('Failed to save permission changes:', err);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    setRoleId(userRole);
    fetchRightsMenu(userRole);
    fetchRoleResponse();
  }, []);

  // Filtered data
  const filteredData = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return screens;
    return screens.filter((row) => {
      const normalizedValues = Object.entries(row).map(([key, val]) => {
        if (key === 'isActive')
          return typeof val === 'boolean'
            ? val
              ? 'active'
              : 'inactive'
            : String(val).toLowerCase();
        return String(val).toLowerCase();
      });
      return normalizedValues.some((v) => v.includes(q));
    });
  }, [searchQuery, screens]);

  const changedCount = menuItems.length;

  const handleCallTypeChange = (event, value) => {
    const roleGUID = value?.globalTypeID || 0;
    setRoleId(roleGUID);
    fetchRightsMenu(roleGUID);
  };

  const fetchRoleResponse = async () => {
    setIsLoading(true);
    try {
      const response = await fetchGlobalTypesCombo(MastersId.USER_ROLE);
      setRoles(response || []);
    } catch (err) {
      console.log('Error fetching installation list:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-100">
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="text.primary">
          Role Access
        </Typography>
      </Box>
      {/* ── Toolbar ───────────────────────────────────────────────────── */}
      <div className="d-flex align-items-center justify-content-between mb-3 gap-2 flex-wrap">
        {/* <Search
          width={isMobile ? '170px' : '280px'}
          height="36px"
          placeholder="Search by menu, item or permission..."
          debounce={250}
          onSearch={setSearchQuery}
          onClear={() => setSearchQuery('')}
        /> */}
        <Autocomplete
          size="small"
          fullWidth
          options={roles}
          value={roles.find((u) => u.globalTypeID === roleId) || null}
          onChange={handleCallTypeChange}
          disablePortal={false}
          disableClearable={false}
          getOptionLabel={(option) => option?.globalTypeName || ''}
          isOptionEqualToValue={(option, value) => option?.globalTypeID === value?.globalTypeID}
          sx={{ width: '30%' }} // adjust width here
          renderInput={(params) => (
            <TextField {...params} sx={FilterFieldSx} placeholder="Select Role" />
          )}
        />

        <div className="d-flex align-items-center gap-2">
          {/* <ExportExcel
            data={screens}
            fileName={`RoleAccess_${new Date().toISOString().slice(0, 10)}.xlsx`}
            sheetName={'RoleAccess'}
            buttonLabel="Excel"
            exportKeys={headers.map((h) => h.key)}
            exportHeaders={headers.map((h) => h.label)}
          /> */}

          <Button
            variant="contained"
            size="small"
            onClick={saveChanges}
            disabled={!canEdit || isSaving || changedCount === 0}
            sx={{
              background: 'var(--primary, #36c95f)',
              '&:hover': { background: 'var(--primary-dark, #27ae60)' },
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 2,
              py: 0.8,
              fontSize: '0.83rem',
              boxShadow: '0 4px 12px rgba(46,204,113,0.28)',
              display: canEdit ? 'inline-flex' : 'none'
            }}
          >
            {isSaving ? 'Saving...' : `Save Changes${changedCount > 0 ? ` (${changedCount})` : ''}`}
          </Button>
        </div>
      </div>

      {/* ── Data Table ────────────────────────────────────────────────── */}
      <DataTable
        headers={headers}
        data={filteredData}
        isLoading={isLoading || isSaving}
        selectable={false}
        emptyMessage={
          searchQuery
            ? `No results for "${searchQuery}"`
            : 'No role permissions found in the system.'
        }
        enablePagination={true}
        zebra={false}
      />
    </div>
  );
};

export default RoleList;
